import { useState, useMemo } from 'react';
import {
  Container,
  Stack,
  Button,
  Modal,
  Text,
  Group,
  Table,
  Checkbox,
  ActionIcon,
  TextInput,
  Alert,
  Select,
  CopyButton,
  Tooltip,
  Paper,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconPlus, IconTrash, IconAlertCircle, IconEdit, IconCopy, IconCheck, IconExternalLink } from '@tabler/icons-react';
import { useBookmarks, useCreateBookmark, useUpdateBookmark, useDeleteBookmark, useBulkDeleteBookmarks, useBulkArchiveBookmarks } from '../api/bookmarks';
import { PageHeader } from '../components/common/PageHeader';
import './BookmarksPage.css';

export default function BookmarksPage() {
  const { data: bookmarks = [], isLoading } = useBookmarks();
  const createBookmarkMutation = useCreateBookmark();
  const updateBookmarkMutation = useUpdateBookmark('');
  const deleteBookmarkMutation = useDeleteBookmark('');
  const bulkDeleteMutation = useBulkDeleteBookmarks();
  const bulkArchiveMutation = useBulkArchiveBookmarks();
  const isDesktop = useMediaQuery('(min-width: 1408px)');

  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingBookmarkId, setEditingBookmarkId] = useState<string | null>(null);

  // Form states
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [status, setStatus] = useState<'draft' | 'archived'>('draft');
  const [category, setCategory] = useState<'uncategorized' | 'job'>('uncategorized');

  // Filter states
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);

  // Selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Filter bookmarks
  const filteredBookmarks = useMemo(() => {
    return bookmarks.filter((bookmark) => {
      if (statusFilter && bookmark.status !== statusFilter) return false;
      if (categoryFilter && bookmark.category !== categoryFilter) return false;
      return true;
    });
  }, [bookmarks, statusFilter, categoryFilter]);

  const editingBookmark = bookmarks.find((b) => b.id === editingBookmarkId);

  const handleCreateBookmark = async () => {
    if (!url.trim()) return;

    try {
      await createBookmarkMutation.mutateAsync({
        url: url.trim(),
        title: title.trim() || undefined,
        status,
        category,
      });
      setUrl('');
      setTitle('');
      setStatus('draft');
      setCategory('uncategorized');
      setCreateModalOpen(false);
    } catch (error) {
      console.error('Error creating bookmark:', error);
    }
  };

  const handleEditBookmark = async () => {
    if (!editingBookmarkId || !url.trim()) return;

    try {
      await updateBookmarkMutation.mutateAsync({
        url: url.trim(),
        title: title.trim() || undefined,
        status,
        category,
      });
      setEditModalOpen(false);
      setEditingBookmarkId(null);
      setUrl('');
      setTitle('');
      setStatus('draft');
      setCategory('uncategorized');
    } catch (error) {
      console.error('Error updating bookmark:', error);
    }
  };

  const handleOpenEditModal = (bookmarkId: string) => {
    const bookmark = bookmarks.find((b) => b.id === bookmarkId);
    if (bookmark) {
      setEditingBookmarkId(bookmarkId);
      setUrl(bookmark.url);
      setTitle(bookmark.title || '');
      setStatus(bookmark.status as any);
      setCategory(bookmark.category as any);
      setEditModalOpen(true);
    }
  };

  const handleSelectBookmark = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.size === filteredBookmarks.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredBookmarks.map((b) => b.id)));
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteBookmarkMutation.mutateAsync();
    } catch (error) {
      console.error('Error deleting bookmark:', error);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;

    try {
      await bulkDeleteMutation.mutateAsync(Array.from(selectedIds));
      setSelectedIds(new Set());
    } catch (error) {
      console.error('Error bulk deleting bookmarks:', error);
    }
  };

  const handleBulkArchive = async () => {
    if (selectedIds.size === 0) return;

    try {
      await bulkArchiveMutation.mutateAsync(Array.from(selectedIds));
      setSelectedIds(new Set());
    } catch (error) {
      console.error('Error bulk archiving bookmarks:', error);
    }
  };

  const formatDate = (isoString: string) => {
    return new Date(isoString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const truncateUrl = (urlString: string, maxLength = 50) => {
    return urlString.length > maxLength ? urlString.substring(0, maxLength) + '...' : urlString;
  };

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <PageHeader
          title="Bookmarks"
          subtitle="Save and organize useful links."
          actions={
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => {
                setUrl('');
                setTitle('');
                setStatus('draft');
                setCategory('uncategorized');
                setCreateModalOpen(true);
              }}
              variant="filled"
            >
              Add Bookmark
            </Button>
          }
        />

        {isDesktop ? (
          <Paper p="md" radius="md" withBorder>
            <Stack gap="md">
              <Group>
                <Select
                  label="Status"
                  placeholder="All statuses"
                  data={[
                    { value: 'draft', label: 'Draft' },
                    { value: 'archived', label: 'Archived' },
                  ]}
                  value={statusFilter}
                  onChange={setStatusFilter}
                  clearable
                  w={150}
                />
                <Select
                  label="Category"
                  placeholder="All categories"
                  data={[
                    { value: 'uncategorized', label: 'Uncategorized' },
                    { value: 'job', label: 'Job' },
                  ]}
                  value={categoryFilter}
                  onChange={setCategoryFilter}
                  clearable
                  w={150}
                />
              </Group>

              {/* Bulk Actions Alert */}
              {selectedIds.size > 0 && (
                <Alert
                  icon={<IconAlertCircle size={16} />}
                  color="blue"
                  title={`${selectedIds.size} bookmark${selectedIds.size !== 1 ? 's' : ''} selected`}
                >
                  <Group gap="xs">
                    <Button
                      size="xs"
                      variant="default"
                      loading={bulkArchiveMutation.isPending}
                      onClick={handleBulkArchive}
                    >
                      Archive
                    </Button>
                    <Button
                      size="xs"
                      color="red"
                      loading={bulkDeleteMutation.isPending}
                      onClick={handleBulkDelete}
                    >
                      Delete
                    </Button>
                    <Button
                      size="xs"
                      variant="subtle"
                      onClick={() => setSelectedIds(new Set())}
                    >
                      Cancel
                    </Button>
                  </Group>
                </Alert>
              )}

              {/* Bookmarks Table */}
              {filteredBookmarks.length === 0 ? (
                <Text c="dimmed" ta="center" py="lg">
                  {bookmarks.length === 0
                    ? 'No bookmarks yet. Add one to get started.'
                    : 'No bookmarks match the selected filters.'}
                </Text>
              ) : (
                <Table striped>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ width: '40px' }}>
                        <Checkbox
                          checked={selectedIds.size === filteredBookmarks.length && filteredBookmarks.length > 0}
                          indeterminate={selectedIds.size > 0 && selectedIds.size < filteredBookmarks.length}
                          onChange={handleSelectAll}
                        />
                      </Table.Th>
                      <Table.Th>URL</Table.Th>
                      <Table.Th style={{ width: '100px' }}>Status</Table.Th>
                      <Table.Th style={{ width: '120px' }}>Category</Table.Th>
                      <Table.Th style={{ width: '120px' }}>Added</Table.Th>
                      <Table.Th style={{ width: '130px' }}>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {filteredBookmarks.map((bookmark) => (
                      <Table.Tr key={bookmark.id}>
                        <Table.Td>
                          <Checkbox
                            checked={selectedIds.has(bookmark.id)}
                            onChange={() => handleSelectBookmark(bookmark.id)}
                          />
                        </Table.Td>
                        <Table.Td>
                          <Tooltip label={bookmark.url} position="top" multiline maw={300}>
                            <Group gap="xs">
                              <Text
                                style={{ cursor: 'pointer', color: 'var(--mantine-color-blue-6)' }}
                                onClick={() => window.open(bookmark.url, '_blank')}
                              >
                                {truncateUrl(bookmark.url)}
                              </Text>
                            </Group>
                          </Tooltip>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed" tt="capitalize">
                            {bookmark.status}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed" tt="capitalize">
                            {bookmark.category}
                          </Text>
                        </Table.Td>
                        <Table.Td>{formatDate(bookmark.created_at)}</Table.Td>
                        <Table.Td>
                          <Group gap={4}>
                            <ActionIcon
                              variant="subtle"
                              size="sm"
                              onClick={() => handleOpenEditModal(bookmark.id)}
                              title="Edit"
                            >
                              <IconEdit size={16} />
                            </ActionIcon>
                            <CopyButton value={bookmark.url} timeout={2000}>
                              {({ copied, copy }) => (
                                <ActionIcon
                                  color={copied ? 'teal' : 'gray'}
                                  variant="subtle"
                                  onClick={copy}
                                  size="sm"
                                  title="Copy URL"
                                >
                                  {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                                </ActionIcon>
                              )}
                            </CopyButton>
                            <ActionIcon
                              variant="subtle"
                              size="sm"
                              color="red"
                              onClick={() => handleDelete(bookmark.id)}
                              title="Delete"
                            >
                              <IconTrash size={16} />
                            </ActionIcon>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Paper>
        ) : (
          <>
            <Group>
              <Select
                label="Status"
                placeholder="All statuses"
                data={[
                  { value: 'draft', label: 'Draft' },
                  { value: 'archived', label: 'Archived' },
                ]}
                value={statusFilter}
                onChange={setStatusFilter}
                clearable
                w={150}
              />
              <Select
                label="Category"
                placeholder="All categories"
                data={[
                  { value: 'uncategorized', label: 'Uncategorized' },
                  { value: 'job', label: 'Job' },
                ]}
                value={categoryFilter}
                onChange={setCategoryFilter}
                clearable
                w={150}
              />
            </Group>

            {/* Bulk Actions Alert */}
            {selectedIds.size > 0 && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                color="blue"
                title={`${selectedIds.size} bookmark${selectedIds.size !== 1 ? 's' : ''} selected`}
              >
                <Group gap="xs">
                  <Button
                    size="xs"
                    variant="default"
                    loading={bulkArchiveMutation.isPending}
                    onClick={handleBulkArchive}
                  >
                    Archive
                  </Button>
                  <Button
                    size="xs"
                    color="red"
                    loading={bulkDeleteMutation.isPending}
                    onClick={handleBulkDelete}
                  >
                    Delete
                  </Button>
                  <Button
                    size="xs"
                    variant="subtle"
                    onClick={() => setSelectedIds(new Set())}
                  >
                    Cancel
                  </Button>
                </Group>
              </Alert>
            )}

            {/* Bookmarks Table */}
            {filteredBookmarks.length === 0 ? (
              <Text c="dimmed" ta="center" py="lg">
                {bookmarks.length === 0
                  ? 'No bookmarks yet. Add one to get started.'
                  : 'No bookmarks match the selected filters.'}
              </Text>
            ) : (
              <Table striped>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ width: '40px' }}>
                      <Checkbox
                        checked={selectedIds.size === filteredBookmarks.length && filteredBookmarks.length > 0}
                        indeterminate={selectedIds.size > 0 && selectedIds.size < filteredBookmarks.length}
                        onChange={handleSelectAll}
                      />
                    </Table.Th>
                    <Table.Th>URL</Table.Th>
                    <Table.Th style={{ width: '100px' }}>Status</Table.Th>
                    <Table.Th style={{ width: '120px' }}>Category</Table.Th>
                    <Table.Th style={{ width: '120px' }}>Added</Table.Th>
                    <Table.Th style={{ width: '130px' }}>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {filteredBookmarks.map((bookmark) => (
                    <Table.Tr key={bookmark.id}>
                      <Table.Td>
                        <Checkbox
                          checked={selectedIds.has(bookmark.id)}
                          onChange={() => handleSelectBookmark(bookmark.id)}
                        />
                      </Table.Td>
                      <Table.Td>
                        <Tooltip label={bookmark.url} position="top" multiline maw={300}>
                          <Group gap="xs">
                            <Text
                              style={{ cursor: 'pointer', color: 'var(--mantine-color-blue-6)' }}
                              onClick={() => window.open(bookmark.url, '_blank')}
                            >
                              {truncateUrl(bookmark.url)}
                            </Text>
                          </Group>
                        </Tooltip>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed" tt="capitalize">
                          {bookmark.status}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed" tt="capitalize">
                          {bookmark.category}
                        </Text>
                      </Table.Td>
                      <Table.Td>{formatDate(bookmark.created_at)}</Table.Td>
                      <Table.Td>
                        <Group gap={4}>
                          <ActionIcon
                            variant="subtle"
                            size="sm"
                            onClick={() => handleOpenEditModal(bookmark.id)}
                            title="Edit"
                          >
                            <IconEdit size={16} />
                          </ActionIcon>
                          <CopyButton value={bookmark.url} timeout={2000}>
                            {({ copied, copy }) => (
                              <ActionIcon
                                color={copied ? 'teal' : 'gray'}
                                variant="subtle"
                                onClick={copy}
                                size="sm"
                                title="Copy URL"
                              >
                                {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                              </ActionIcon>
                            )}
                          </CopyButton>
                          <ActionIcon
                            variant="subtle"
                            size="sm"
                            color="red"
                            onClick={() => handleDelete(bookmark.id)}
                            title="Delete"
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </>
        )}
      </Stack>

      {/* Create Bookmark Modal */}
      <Modal
        opened={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        title="Add Bookmark"
        size="md"
      >
        <Stack gap="md">
          <TextInput
            label="URL"
            placeholder="https://example.com"
            value={url}
            onChange={(e) => setUrl(e.currentTarget.value)}
            required
          />
          <TextInput
            label="Title (Optional)"
            placeholder="Bookmark title"
            value={title}
            onChange={(e) => setTitle(e.currentTarget.value)}
          />
          <Select
            label="Status"
            data={[
              { value: 'draft', label: 'Draft' },
              { value: 'archived', label: 'Archived' },
            ]}
            value={status}
            onChange={(val) => setStatus((val as any) || 'draft')}
          />
          <Select
            label="Category"
            data={[
              { value: 'uncategorized', label: 'Uncategorized' },
              { value: 'job', label: 'Job' },
            ]}
            value={category}
            onChange={(val) => setCategory((val as any) || 'uncategorized')}
          />
          <Group justify="flex-end" gap="xs">
            <Button variant="subtle" onClick={() => setCreateModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateBookmark}
              loading={createBookmarkMutation.isPending}
              disabled={!url.trim()}
            >
              Add
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Edit Bookmark Modal */}
      <Modal
        opened={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title="Edit Bookmark"
        size="md"
      >
        <Stack gap="md">
          <TextInput
            label="URL"
            placeholder="https://example.com"
            value={url}
            onChange={(e) => setUrl(e.currentTarget.value)}
            required
          />
          <TextInput
            label="Title (Optional)"
            placeholder="Bookmark title"
            value={title}
            onChange={(e) => setTitle(e.currentTarget.value)}
          />
          <Select
            label="Status"
            data={[
              { value: 'draft', label: 'Draft' },
              { value: 'archived', label: 'Archived' },
            ]}
            value={status}
            onChange={(val) => setStatus((val as any) || 'draft')}
          />
          <Select
            label="Category"
            data={[
              { value: 'uncategorized', label: 'Uncategorized' },
              { value: 'job', label: 'Job' },
            ]}
            value={category}
            onChange={(val) => setCategory((val as any) || 'uncategorized')}
          />
          <Group justify="flex-end" gap="xs">
            <Button variant="subtle" onClick={() => setEditModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleEditBookmark}
              loading={updateBookmarkMutation.isPending}
              disabled={!url.trim()}
            >
              Save Changes
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
